import { apiClient } from '@/app/core/services/apiClient.service';
import { BaseApiService } from '@/app/shared/services/apis/baseApi.service';
import type { ID } from '@fifa-bsm/bo-frontend-shared/utils/types';
import type { ApiResponse } from '@/app/shared/services/apis/types/ApiResponse';
import type { QueryParams } from '@fifa-bsm/bo-frontend-shared';
import type { SeatPlanListItem, ViewPoint } from '@/app/shared/services/apis/types/SeatPlans';
import type { AxiosResponse } from 'axios';
import type { EventWithStats } from '@/app/shared/services/apis/types/Events';

class SeatingApiService extends BaseApiService {
    getVenues() {
        return this.client.get(`venues`);
    }

    getVenue(venueId: ID) {
        return this.client.get(`venues/${venueId}`);
    }

    getSeatCategories() {
        return this.client.get(`venue-seat-categories`);
    }

    saveSeatCategory(seatCategory: any): any {
        return this.client.post(`venue-seat-categories`, seatCategory);
    }

    changeSeatCategory(seatCategory: any): any {
        return this.client.put(`venue-seat-categories/${seatCategory.id}`, seatCategory);
    }

    deleteSeatCategory(seatCategoryId: ID) {
        return this.client.delete(`venue-seat-categories/${seatCategoryId}`);
    }

    getGOAllocationSeatPlan(eventId: ID, seatPlanId: ID, GOSeatPlanId: ID) {
        return this.client.get(
            `seat-allocator/events/${eventId}/seat-plans/${seatPlanId}/guest-operations/${GOSeatPlanId}`
        );
    }

    lockSeat(eventId: ID, seatId: ID) {
        return this.client.post(`seat-allocator/events/${eventId}/locked-seats`, {
            seat_id: seatId,
        });
    }

    unlockSeat(eventId: ID, seatId: ID) {
        return this.client.delete(`seat-allocator/events/${eventId}/locked-seats/${seatId}`);
    }

    getSeatAccessGroups() {
        return this.client.get(`access-groups`);
    }

    getSeatPlans(venueId: ID, query?: QueryParams) {
        return this.client.get<ApiResponse<SeatPlanListItem[]>>(
            `venues/${venueId}/seat-plans`,
            query
        );
    }

    getSeatPlan(venueId: ID, seatPlanId: ID) {
        return this.client.get(`venues/${venueId}/seat-plans/${seatPlanId}`);
    }

    duplicateSeatPlan(venueId: ID, newSeatPlanName: string, seatPlanId: ID) {
        return this.client.post(`venues/${venueId}/seat-plans/duplicate`, {
            venue_seat_plan_id: seatPlanId,
            name: newSeatPlanName,
        });
    }

    changeSeatPlan(venueId: ID, seatPlanId: ID, seatPlan: any) {
        return this.client.put(`venues/${venueId}/seat-plans/${seatPlanId}`, seatPlan);
    }

    updateSeatPlanWithSecutix(venueId: ID, seatPlanId: ID) {
        return this.client.put(`venues/${venueId}/seat-plans/${seatPlanId}/update-all-seats`);
    }

    getSeatPlanTypes() {
        return this.client.get(`venue-seat-plan-types`);
    }

    getBlocks(venueId: ID, seatPlanId: ID) {
        return this.client.get(`venues/${venueId}/seat-plans/${seatPlanId}/blocks`);
    }

    getRows(venueId: ID, seatPlanId: ID, blockId: ID) {
        return this.client.get(`venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows`);
    }

    getRow(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID) {
        return this.client.get(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}`
        );
    }

    changeSeat(eventId: ID, journalistId: ID, seatId: ID) {
        return this.client.put(`events/${eventId}/seats-allocation/${journalistId}/seat`, {
            data: { seat: seatId },
        });
    }

    saveViewPoint(eventId: ID, seatPlanId: ID, pointView: ViewPoint) {
        return this.client.post(
            `venues/${eventId}/seat-plans/${seatPlanId}/starting-points`,
            pointView
        );
    }

    changeViewPoint(eventId: ID, seatPlanId: ID, pointView: ViewPoint, pointViewId: ID) {
        return this.client.put(
            `venues/${eventId}/seat-plans/${seatPlanId}/starting-points/${pointViewId}`,
            pointView
        );
    }

    getSeats(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID) {
        return this.client.get(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}/seats`
        );
    }

    getSeat(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID, seatId: ID) {
        return this.client.get(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}/seats/${seatId}`
        );
    }

    createSeat(
        venueId: ID,
        seatPlanId: ID,
        blockId: ID,
        rowId: ID,
        data: {
            x: number;
            y: number;
            rotation: number;
            color?: string | null;
            category_id: number | null;
            gate: string | null;
            venue_seat_type_id: number;
            label?: string | null;
            label_color?: string | null;
            name: string;
            quality?: number | null;
        }
    ) {
        return this.client.post(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}/seats`,
            data
        );
    }

    updateSeat(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID, seatId: ID, data: any) {
        return this.client.put(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}/seats/${seatId}`,
            data
        );
    }

    removeSeat(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID, seatId: ID) {
        return this.client.delete(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}/seats/${seatId}`
        );
    }

    createBlock(venueId: ID, seatPlanId: ID, payload: any) {
        return this.client.post(`venues/${venueId}/seat-plans/${seatPlanId}/blocks`, payload);
    }

    updateBlock(venueId: ID, seatPlanId: ID, blockId: ID, data: any) {
        return this.client.put(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}`,
            data
        );
    }

    removeBlock(venueId: ID, seatPlanId: ID, blockId: ID) {
        return this.client.delete(`venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}`);
    }

    createRow(venueId: ID, seatPlanId: ID, blockId: ID, data: any) {
        return this.client.post(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows`,
            data
        );
    }

    updateRow(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID, data: any) {
        return this.client.put(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}`,
            data
        );
    }

    removeRow(venueId: ID, seatPlanId: ID, blockId: ID, rowId: ID) {
        return this.client.delete(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/${rowId}`
        );
    }

    updateSeats(venueId: ID, seatPlanId: ID, data: any) {
        return this.client.patch(
            `venues/${venueId}/seat-plans/${seatPlanId}/batch/update-seats`,
            data
        );
    }

    importSeatplanFromFile(venueId: ID, seatplanId: ID, formData: any) {
        return this.client.post(`venues/${venueId}/seat-plans/${seatplanId}/imports`, formData, {
            headers: {
                'Content-Type': 'text/csv',
            },
        });
    }

    getSeatPlanCategories(venueId: ID, seatplanId: ID) {
        return this.client.get(`venues/${venueId}/seat-plans/${seatplanId}/imports`);
    }

    getVenueSeatTypes() {
        return this.client.get(`venue-seat-types`);
    }

    getVenueSeatGroups() {
        return this.client.get(`venue-seat-groups`);
    }

    customizeSeatplan(venueId: ID, seatplanId: ID, categories: any, rowsOrder: any) {
        return this.client.put(`venues/${venueId}/seat-plans/${seatplanId}/imports/customize`, {
            categories: categories,
            rows_order: rowsOrder,
        });
    }

    uploadImage(venueId: ID, seatPlanId: ID, formData: any) {
        return this.client.post(`venues/${venueId}/seat-plans/${seatPlanId}/images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    updateImage(venueId: ID, seatPlanId: ID, imageId: ID, payload: any) {
        return this.client.patch(
            `venues/${venueId}/seat-plans/${seatPlanId}/images/${imageId}`,
            payload
        );
    }

    removeImage(venueId: ID, seatPlanId: ID, imageId: ID) {
        return this.client.delete(`venues/${venueId}/seat-plans/${seatPlanId}/images/${imageId}`);
    }

    getEditorSeatPlan(venueId: ID, seatPlanId: ID) {
        return this.client.get(`venues/${venueId}/seat-plans/${seatPlanId}/editor`);
    }

    async getPDFTemplatesAssignedToSeats(mainEventId: ID, eventId: ID) {
        return this.client.get(
            `main-events/${mainEventId}/eticketing/dedicated-pdf-templates/${eventId}`
        );
    }

    async markSeatPlanForSeatTags(eventId: ID, selectedSeatTagSeatPlanId: ID): Promise<void> {
        return this.client.patch<void>(
            `seat-allocator/guest-operations/events/${eventId}/seat-plans/change-seat-tag-plan`,
            {
                go_seat_plan_id: selectedSeatTagSeatPlanId,
            }
        );
    }

    async getEventsWithStats(mainEventId: ID, query?: QueryParams | string) {
        return this.client.get<ApiResponse<EventWithStats[]>>(
            `seat-allocator/guest-operations/main-events/${mainEventId}/events`,
            query
        );
    }

    generateSeatPlanFreeSeatsReport(
        eventId: ID,
        selectedSeatTagSeatPlanId: ID,
        fileFormat: string,
        fieldsIncluded: string[]
    ): Promise<AxiosResponse<ArrayBuffer>> {
        return this.client.get<AxiosResponse<ArrayBuffer>>(
            `seat-allocator/guest-operations/events/${eventId}/seat-plans/${selectedSeatTagSeatPlanId}/free-seats-report`,
            {
                report_type: fileFormat,
                data_fields: fieldsIncluded,
            },
            {
                mapResponse: (response: AxiosResponse<ArrayBuffer>) => response,
                responseType: 'arraybuffer',
            }
        );
    }

    generateQuickMatchReport(eventId: ID, seatPlanId: ID): Promise<AxiosResponse<ArrayBuffer>> {
        return this.client.get<AxiosResponse<ArrayBuffer>>(
            `seat-allocator/guest-operations/events/${eventId}/seat-plans/${seatPlanId}/quick-match-report`,
            {},
            {
                mapResponse: (response: AxiosResponse<ArrayBuffer>) => response,
                responseType: 'arraybuffer',
            }
        );
    }

    async removeMultipleSeats(venueId: ID, seatPlanId: ID, seatIds: number[]): Promise<void> {
        return this.client.delete<void>(
            `venues/${venueId}/seat-plans/${seatPlanId}/batch/delete-seats`,
            {
                seat_ids: seatIds,
            }
        );
    }

    async createMultipleRows(
        venueId: ID,
        seatPlanId: ID,
        blockId: ID,
        payload: any
    ): Promise<void> {
        return this.client.post<void>(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/${blockId}/rows/create-many`,
            payload
        );
    }

    copyBlocks(
        venueId: ID,
        seatPlanId: ID,
        payload: {
            blocks: ({
                id: number;
                name: string;
            } & (
                | { starting_x: number; starting_y: number }
                | { offset_x: number; offset_y: number }
            ))[];
            target_venue_seat_plan_id?: number;
        }
    ): Promise<void> {
        return this.client.post<void>(
            `venues/${venueId}/seat-plans/${seatPlanId}/blocks/copy`,
            payload
        );
    }

    copyRows(
        venueId: ID,
        seatPlanId: ID,
        payload: {
            rows: {
                id: number;
                name: string;
                offset_x: number;
                offset_y: number;
            }[];
            target_venue_seat_plan_id: number;
        }
    ): Promise<void> {
        return this.client.post<void>(
            `venues/${venueId}/seat-plans/${seatPlanId}/rows/copy`,
            payload
        );
    }
}

export const seatingApi = new SeatingApiService(apiClient);
